import { Access, Account, Flag, Organization, Service, Variant } from '@acies/domain';
import { IAccessJSON, IAccountJSON, IFeatureFlagsJSON, IFlagJSON, IOrganizationJSON, ISessionAccessJSON, IVariantJSON } from '@acies/schema';
import ParseError from '@shared/errors/parse-error';
import { map } from 'rxjs';
import { ZodType } from 'zod';

/** Transforms account JSON to an instance of Account. */
function accountTransform({ id, name }: IAccountJSON, organization: Organization): Account {
	return new Account({ id, name, organization });
}

/** Transforms organization JSON to an instance of Organization. */
function organizationTransform({ accounts: acc, id, name }: IOrganizationJSON): Organization {
	const organization = new Organization({
		accounts: [],
		id,
		name,
	});
	const accounts = acc.map((json) => accountTransform(json, organization));
	organization.accounts = accounts;
	return organization;
}

/** Transforms top level iam/me/access JSON to an instance of Access. */
export function accessTransform({ result }: IAccessJSON): Access {
	const { id, organizations: org } = result;
	const organizations = org.map((json) => organizationTransform(json));
	return new Access({
		id,
		organizations,
	});
}

/** Transforms session storage JSON to an instance of Access. */
export function sessionAccessTransform({ id, organizations: org }: ISessionAccessJSON): Access {
	const organizations = org.map((json) => organizationTransform(json));
	return new Access({
		id,
		organizations,
	});
}

/**
 * Transforms variant JSON to an instance of Variant.
 *
 * Account[] is needed so the Variant.account can be set to the correct Account instance.
 *
 * This function will throw an Error if the Account instance can't be found in the accounts Map<string, Account> parameter.
 */
function variantTransform({ accountId, enabled }: IVariantJSON, accounts: Map<string, Account>): Variant {
	const account = accounts.get(accountId);
	if (account instanceof Account) {
		return new Variant({
			account,
			enabled,
		});
	}
	/** We throw here because a missing Account instance is considered a fatal defect. */
	throw new Error(`variantTransform Error. Account with id[${accountId}] could not be found.`);
}

/** Transforms flag json to Flag instances with variants. */
function flagTransform({ createdAt, description, enabled, expiresAt, id, service, variants: vars }: IFlagJSON, accounts: Map<string, Account>): Flag {
	const variants = vars.map((json) => variantTransform(json, accounts));
	return new Flag({
		createdAt: new Date(createdAt),
		description,
		enabled,
		expiresAt: new Date(expiresAt),
		name: id,
		service,
		variants,
	});
}

/**
 * Create Services instances forom the featureflags json response.
 *
 * It groups by services so it's easier to create UI lists.
 */
export function transformFeatureFlags(accounts: Map<string, Account>) {
	const servicesMap: Map<string, Service> = new Map();
	return map((data: IFeatureFlagsJSON) => {
		for (const flag of data.result) {
			let service = servicesMap.get(flag.service);
			if (!service) {
				service = new Service({ flags: [], name: flag.service });
				servicesMap.set(service.name, service);
			}
			service.flags.push(flagTransform(flag, accounts));
		}
		return Array.from(servicesMap.values());
	});
}

/** Safely validates the JSON and returns a ParseError if it fails, otherwise the correct schema data. */
export function parse<T>(schema: ZodType<T>) {
	return map((data: unknown) => {
		const parsed = schema.safeParse(data);
		if (!parsed.success) {
			return new ParseError(parsed.error.message);
		}
		return parsed.data;
	});
}
